import type { DesignGroup } from '../types/storeReviewOrderStep.types';
import type { ISizesFormValues } from '../StoreConfirmSizes/helpers/storeConfirmSizes.types';
import { STORE_LAUNCH_DISABLED_MESSAGES } from '../constants/storeCheckout.constants';
import { resolveStoreReviewProductTitle } from './storeCheckoutProductTitleUpdate.utils';
import { hasPositiveDonationAmount } from './storeReviewDonationValidation.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

export type StoreCheckoutStoreType =
  | 'group-order'
  | 'brand-drop'
  | 'fundraiser'
  | 'permanent-store'
  | '';

export interface IStoreCheckoutShippingDetailsInput {
  shippingOption: string;
  shippingName: string;
  street1: string;
  city: string;
  state?: { value?: string; label?: string };
  shippingZipCode: string;
}

export interface IStoreCheckoutLaunchValidationInput {
  isEditPage: boolean;
  hasChanges: boolean;
  storeType: StoreCheckoutStoreType;
  storeName: string;
  storeDescription: string;
  orderUntil: string;
  designGroups: DesignGroup[];
  sizes: ISizesFormValues;
  shippingDetails: IStoreCheckoutShippingDetailsInput;
  donationAmounts: Record<string, string>;
}

export const resolveStoreConfirmSizesOrderItemId = (item: ICartItemDetail): string =>
  String(item.cartItemId ?? item.proofItemId);

export const hasAtLeastOneValidItemDonation = (
  designGroups: DesignGroup[],
  donationAmounts: Record<string, string>
): boolean => {
  for (let groupIndex = 0; groupIndex < designGroups.length; groupIndex += 1) {
    const group = designGroups[groupIndex];
    for (let itemIndex = 0; itemIndex < group.items.length; itemIndex += 1) {
      const donationKey = `donation-${groupIndex}-${itemIndex}`;
      const amount = donationAmounts[donationKey] ?? '';
      if (hasPositiveDonationAmount(amount)) {
        return true;
      }
    }
  }

  return false;
};

export const hasAllStoreReviewProductTitles = (designGroups: DesignGroup[]): boolean => {
  for (let groupIndex = 0; groupIndex < designGroups.length; groupIndex += 1) {
    const group = designGroups[groupIndex];
    for (let itemIndex = 0; itemIndex < group.items.length; itemIndex += 1) {
      const productTitle = resolveStoreReviewProductTitle(group.items[itemIndex]).trim();
      if (!productTitle) {
        return false;
      }
    }
  }

  return true;
};

export const hasAllDesignGroupsPricingSelected = (
  designGroups: DesignGroup[],
  storeType: StoreCheckoutStoreType
): boolean => {
  if (storeType === 'permanent-store') {
    return true;
  }

  return !designGroups.some((group) => group.items.length > 0 && !group.pricingModel);
};

export const hasAllOrderItemsWithSelectedSize = (
  designGroups: DesignGroup[],
  sizes: ISizesFormValues
): boolean => {
  const sizeGroupsByOrderItemId = new Map(sizes.sizes.map((group) => [group.orderItemId, group]));

  return designGroups
    .flatMap((group) => group.items)
    .every((item) => {
      const orderItemId = resolveStoreConfirmSizesOrderItemId(item);
      const sizeGroup = sizeGroupsByOrderItemId.get(orderItemId);

      if (!sizeGroup) {
        return false;
      }

      return Object.values(sizeGroup.sizesSelected).some((size) => size.selected);
    });
};

export const hasCompleteOnePlaceShippingAddress = (
  shipping: IStoreCheckoutShippingDetailsInput
): boolean => {
  if (shipping.shippingOption !== 'one-place') {
    return true;
  }

  const requiredFields = [
    shipping.shippingName,
    shipping.street1,
    shipping.city,
    shipping.state?.value,
    shipping.shippingZipCode,
  ];

  return requiredFields.every((field) => Boolean(field?.trim()));
};

/** True when any store checkout item is blocked for self-checkout / licensing. */
export const hasStoreCheckoutBlockedSelfCheckoutItems = (designGroups: DesignGroup[]): boolean =>
  designGroups.some((group) =>
    group.items.some((item) => {
      const rejection = item.designRejectionInfo;
      if (rejection) {
        return rejection.isDesignSelfBlockedForCheckout || rejection.isDesignLicensingRejected;
      }

      // Fallback when hydration skipped attaching designRejectionInfo
      return Boolean(item.designPricing?.block_self_checkout);
    })
  );

export const isStoreShippingInfoSectionComplete = (
  shipping: IStoreCheckoutShippingDetailsInput
): boolean => {
  const shippingOption = shipping.shippingOption?.trim();

  if (!shippingOption) {
    return false;
  }

  if (shippingOption === 'direct') {
    return true;
  }

  return hasCompleteOnePlaceShippingAddress(shipping);
};

export const getStoreLaunchDisabledReason = (
  input: IStoreCheckoutLaunchValidationInput
): string => {
  if (hasStoreCheckoutBlockedSelfCheckoutItems(input.designGroups)) {
    return STORE_LAUNCH_DISABLED_MESSAGES.BLOCKED_SELF_CHECKOUT;
  }

  // Edit save still requires all launch fields; having changes alone is not enough.
  if (input.isEditPage && !input.hasChanges) {
    return STORE_LAUNCH_DISABLED_MESSAGES.NO_CHANGES_TO_SAVE;
  }

  if (!input.storeType) {
    return STORE_LAUNCH_DISABLED_MESSAGES.STORE_TYPE_REQUIRED;
  }

  const storeName = input.storeName?.trim() ?? '';
  if (!storeName) {
    return STORE_LAUNCH_DISABLED_MESSAGES.STORE_NAME_REQUIRED;
  }

  const storeDescription = input.storeDescription?.trim() ?? '';
  if (!storeDescription) {
    return STORE_LAUNCH_DISABLED_MESSAGES.STORE_DESCRIPTION_REQUIRED;
  }

  if (input.storeType !== 'permanent-store') {
    const orderUntil = input.orderUntil?.trim() ?? '';
    if (!orderUntil) {
      return STORE_LAUNCH_DISABLED_MESSAGES.ORDER_UNTIL_REQUIRED;
    }
  }

  if (!hasAllStoreReviewProductTitles(input.designGroups)) {
    return STORE_LAUNCH_DISABLED_MESSAGES.PRODUCT_TITLE_REQUIRED;
  }

  if (!hasAllDesignGroupsPricingSelected(input.designGroups, input.storeType)) {
    return STORE_LAUNCH_DISABLED_MESSAGES.PRICING_OPTION_REQUIRED;
  }

  if (!hasAllOrderItemsWithSelectedSize(input.designGroups, input.sizes)) {
    return STORE_LAUNCH_DISABLED_MESSAGES.SIZE_SELECTION_REQUIRED;
  }

  if (!hasCompleteOnePlaceShippingAddress(input.shippingDetails)) {
    return STORE_LAUNCH_DISABLED_MESSAGES.SHIPPING_ADDRESS_REQUIRED;
  }

  if (
    input.storeType === 'fundraiser' &&
    !hasAtLeastOneValidItemDonation(input.designGroups, input.donationAmounts)
  ) {
    return STORE_LAUNCH_DISABLED_MESSAGES.DONATION_AMOUNT_REQUIRED;
  }

  if (
    input.storeType === 'brand-drop' &&
    !hasAtLeastOneValidItemDonation(input.designGroups, input.donationAmounts)
  ) {
    return STORE_LAUNCH_DISABLED_MESSAGES.MARKUP_AMOUNT_REQUIRED;
  }

  return '';
};

export const canLaunchStore = (input: IStoreCheckoutLaunchValidationInput): boolean =>
  getStoreLaunchDisabledReason(input) === '';

export const getStoreCheckoutLaunchTooltipTitle = (
  canLaunch: boolean,
  launchDisabledReason: string
): string => {
  if (!canLaunch && launchDisabledReason) {
    return launchDisabledReason;
  }

  return '';
};
